import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { getTokenInfo } from "../../utils/auth";
import { getAdminMe } from "../../services/api";
import {
  Building2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  ChevronRight,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  SlidersHorizontal,
  X,
} from "lucide-react";
import api from "../../services/api";
import { toast } from "react-hot-toast";
import { activeCurrencyCode } from "../../utils/currency";

// ─── Types ───────────────────────────────────────────────────────────────────

interface RestaurantSummary {
  restaurantId: string;
  hotelName: string;
  address: string;
  totalDebit: number;
  totalCredit: number;
  balance: number;
  lastEntry: string | null;
}

interface LedgerEntry {
  _id: string;
  type: "order" | "bonus" | "settlement" | "adjustment";
  direction: "debit" | "credit";
  amount: number;
  description: string;
  adminNote?: string;
  createdAt: string;
  userId?: { name: string; phonenumber: string; email: string };
  orderId?: string;
}

interface LedgerData {
  entries: LedgerEntry[];
  total: number;
  page: number;
  pages: number;
  ordersSummary: { totalAmount: number; count: number };
  periodSummary: {
    orderRevenue: number;
    orderCount: number;
    loyaltyDebits: number;
    walletSpendCredits: number;
    totalDebit: number;
    totalCredit: number;
    balance: number;
  };
  allTimeSummary: {
    orderRevenue: number;
    orderCount: number;
    loyaltyDebits: number;
    walletSpendCredits: number;
    totalDebit: number;
    totalCredit: number;
    balance: number;
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n: number, currency = activeCurrencyCode()) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n);

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const typeConfig: Record<
  string,
  { label: string; color: string; bg: string }
> = {
  order: { label: "Order", color: "text-blue-700", bg: "bg-blue-100" },
  bonus: { label: "Bonus", color: "text-purple-700", bg: "bg-purple-100" },
  settlement: { label: "Settlement", color: "text-green-700", bg: "bg-green-100" },
  adjustment: { label: "Adjustment", color: "text-orange-700", bg: "bg-orange-100" },
};

// ─── Summary View ─────────────────────────────────────────────────────────────

const SummaryView: React.FC<{
  summaries: RestaurantSummary[];
  loading: boolean;
  onSelect: (r: RestaurantSummary) => void;
  onRefresh: () => void;
}> = ({ summaries, loading, onSelect, onRefresh }) => {
  const { currency } = useAuth();
  const totalOwed = summaries.reduce((s, r) => s + Math.max(0, r.balance), 0);
  const totalRestaurants = summaries.length;
  const debtors = summaries.filter((r) => r.balance > 0).length;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Restaurant Ledger</h1>
          <p className="text-sm text-gray-500 mt-1">Track cash flow & settle balances</p>
        </div>
        <button
          onClick={onRefresh}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Total Owed to Admin</p>
          <p className="text-2xl font-bold text-red-600 mt-1">{fmt(totalOwed, currency)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Restaurants</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{totalRestaurants}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">With Outstanding Balance</p>
          <p className="text-2xl font-bold text-orange-600 mt-1">{debtors}</p>
        </div>
      </div>

      {/* Restaurant list */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">All Restaurants</h2>
        </div>
        {loading ? (
          <div className="p-12 text-center text-gray-400">Loading...</div>
        ) : summaries.length === 0 ? (
          <div className="p-12 text-center text-gray-400">No restaurants found</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {summaries.map((r) => (
              <button
                key={r.restaurantId}
                onClick={() => onSelect(r)}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{r.hotelName}</p>
                    <p className="text-xs text-gray-500">{r.address}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Balance</p>
                    <p
                      className={`font-semibold text-sm ${
                        r.balance > 0
                          ? "text-red-600"
                          : r.balance < 0
                          ? "text-green-600"
                          : "text-gray-500"
                      }`}
                    >
                      {r.balance > 0 ? "Owes " : r.balance < 0 ? "Credit " : ""}
                      {fmt(Math.abs(r.balance), currency)}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Settle Modal ─────────────────────────────────────────────────────────────

const SettleModal: React.FC<{
  restaurantId: string;
  hotelName: string;
  currentBalance: number;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ restaurantId, hotelName, currentBalance, onClose, onSuccess }) => {
  const { currency } = useAuth();
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    try {
      setSaving(true);
      await api.post(`/ledger/restaurant/${restaurantId}/settle`, {
        amount: amt,
        adminNote: note || undefined,
      });
      toast.success("Settlement recorded");
      onSuccess();
      onClose();
    } catch {
      toast.error("Failed to record settlement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-bold text-gray-900">Record Settlement</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <p className="text-sm text-gray-500">Restaurant</p>
            <p className="font-semibold text-gray-900">{hotelName}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Outstanding Balance</p>
            <p className={`font-bold text-lg ${currentBalance > 0 ? "text-red-600" : "text-green-600"}`}>
              {fmt(currentBalance, currency)}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount Received
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note (optional)
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Weekly wire transfer"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Record Settlement"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Adjustment Modal ─────────────────────────────────────────────────────────

const AdjustmentModal: React.FC<{
  restaurantId: string;
  hotelName: string;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ restaurantId, hotelName, onClose, onSuccess }) => {
  const [amount, setAmount] = useState("");
  const [direction, setDirection] = useState<"debit" | "credit">("debit");
  const [description, setDescription] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!description.trim()) { toast.error("Description is required"); return; }
    try {
      setSaving(true);
      await api.post(`/ledger/restaurant/${restaurantId}/adjustment`, {
        amount: amt,
        direction,
        description: description.trim(),
        adminNote: note || undefined,
      });
      toast.success("Adjustment recorded");
      onSuccess();
      onClose();
    } catch {
      toast.error("Failed to record adjustment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-bold text-gray-900">Manual Adjustment — {hotelName}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setDirection("debit")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border ${
                direction === "debit"
                  ? "bg-red-600 text-white border-red-600"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              Debit (owes more)
            </button>
            <button
              type="button"
              onClick={() => setDirection("credit")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border ${
                direction === "credit"
                  ? "bg-green-600 text-white border-green-600"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              Credit (reduces debt)
            </button>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
            <input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Reason for adjustment" required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? "Saving..." : "Record Adjustment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Restaurant Detail View ───────────────────────────────────────────────────

const RestaurantLedgerView: React.FC<{
  restaurant: RestaurantSummary;
  onBack?: () => void;
  readOnly?: boolean;
}> = ({ restaurant, onBack, readOnly = false }) => {
  const { currency } = useAuth();
  const [ledger, setLedger] = useState<LedgerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState("");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [showSettle, setShowSettle] = useState(false);
  const [showAdjustment, setShowAdjustment] = useState(false);
  const [allTimeSummary, setAllTimeSummary] = useState<LedgerData["allTimeSummary"] | null>(null);

  const fetchLedger = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = { page: String(page), limit: "50" };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (typeFilter) params.type = typeFilter;

      const res = await api.get(`/ledger/restaurant/${restaurant.restaurantId}`, { params });
      if (res.data.success) {
        setLedger(res.data.data);
        setAllTimeSummary(res.data.data.allTimeSummary);
      }
    } catch {
      toast.error("Failed to load ledger");
    } finally {
      setLoading(false);
    }
  }, [restaurant.restaurantId, page, startDate, endDate, typeFilter]);

  useEffect(() => { fetchLedger(); }, [fetchLedger]);

  const handleRefresh = () => { setPage(1); fetchLedger(); };

  return (
    <div className="p-6 space-y-6">
      {showSettle && (
        <SettleModal
          restaurantId={restaurant.restaurantId}
          hotelName={restaurant.hotelName}
          currentBalance={allTimeSummary?.balance ?? restaurant.balance}
          onClose={() => setShowSettle(false)}
          onSuccess={handleRefresh}
        />
      )}
      {showAdjustment && (
        <AdjustmentModal
          restaurantId={restaurant.restaurantId}
          hotelName={restaurant.hotelName}
          onClose={() => setShowAdjustment(false)}
          onSuccess={handleRefresh}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          {onBack && (
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
          )}
          <div>
            <h1 className="text-xl font-bold text-gray-900">{restaurant.hotelName}</h1>
            <p className="text-xs text-gray-500">{restaurant.address}</p>
          </div>
        </div>
        {!readOnly && (
        <div className="flex gap-2">
          <button
            onClick={() => setShowAdjustment(true)}
            className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium flex items-center gap-2"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Adjust
          </button>
          <button
            onClick={() => setShowSettle(true)}
            className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2"
          >
            <CheckCircle className="w-4 h-4" />
            Settle
          </button>
        </div>
        )}
      </div>

      {/* All-time balance cards */}
      {allTimeSummary && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-blue-500" />
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide m-0">Order Revenue</p>
            </div>
            <p className="text-2xl font-bold text-blue-700">{fmt(allTimeSummary.orderRevenue, currency)}</p>
            <p className="text-xs text-gray-400 mt-1">{allTimeSummary.orderCount} orders</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-purple-500" />
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide m-0">Bonuses Issued</p>
            </div>
            <p className="text-2xl font-bold text-purple-600">{fmt(allTimeSummary.loyaltyDebits, currency)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown className="w-4 h-4 text-green-500" />
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide m-0">Total Settled</p>
            </div>
            <p className="text-2xl font-bold text-green-600">{fmt(allTimeSummary.totalCredit, currency)}</p>
          </div>
          <div
            className={`rounded-xl border p-5 shadow-sm ${
              allTimeSummary.balance > 0 ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className={`w-4 h-4 ${allTimeSummary.balance > 0 ? "text-red-500" : "text-green-500"}`} />
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 m-0">Outstanding Balance</p>
            </div>
            <p className={`text-2xl font-bold ${allTimeSummary.balance > 0 ? "text-red-700" : "text-green-700"}`}>
              {allTimeSummary.balance > 0 ? "Owes " : allTimeSummary.balance < 0 ? "Credit " : ""}
              {fmt(Math.abs(allTimeSummary.balance), currency)}
            </p>
            {allTimeSummary.balance > 0 && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Payment pending
              </p>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="order">Orders</option>
              <option value="bonus">Bonuses</option>
              <option value="settlement">Settlements</option>
              <option value="adjustment">Adjustments</option>
            </select>
          </div>
          <button
            onClick={handleRefresh}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Apply
          </button>
        </div>

        {/* Period summary */}
        {ledger && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-6 text-sm">
            <span className="text-gray-500">
              Orders: <span className="font-semibold text-blue-700">{fmt(ledger.periodSummary.orderRevenue, currency)}</span>
              <span className="text-gray-400"> ({ledger.periodSummary.orderCount})</span>
            </span>
            <span className="text-gray-500">
              Bonuses: <span className="font-semibold text-purple-600">{fmt(ledger.periodSummary.loyaltyDebits, currency)}</span>
            </span>
            <span className="text-gray-500">
              Settled: <span className="font-semibold text-green-600">{fmt(ledger.periodSummary.totalCredit, currency)}</span>
            </span>
            <span className="text-gray-500">
              Net owed: <span className={`font-semibold ${ledger.periodSummary.balance >= 0 ? "text-red-600" : "text-green-600"}`}>
                {fmt(ledger.periodSummary.balance, currency)}
              </span>
            </span>
          </div>
        )}
      </div>

      {/* Transaction table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">Transactions</h2>
        </div>
        {loading ? (
          <div className="p-12 text-center text-gray-400">Loading...</div>
        ) : !ledger || ledger.entries.length === 0 ? (
          <div className="p-12 text-center text-gray-400">No transactions for this period</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-6 py-3 font-medium text-gray-500">Date</th>
                    <th className="text-left px-6 py-3 font-medium text-gray-500">Type</th>
                    <th className="text-left px-6 py-3 font-medium text-gray-500">Description</th>
                    <th className="text-right px-6 py-3 font-medium text-gray-500">Amount</th>
                    <th className="text-right px-6 py-3 font-medium text-gray-500">Direction</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {ledger.entries.map((entry) => {
                    const tc = typeConfig[entry.type] || typeConfig.adjustment;
                    return (
                      <tr key={entry._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 text-gray-500 whitespace-nowrap">
                          {fmtDate(entry.createdAt)}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tc.bg} ${tc.color}`}>
                            {tc.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-800">
                          <p>{entry.description}</p>
                          {entry.adminNote && (
                            <p className="text-xs text-gray-400 mt-0.5">Note: {entry.adminNote}</p>
                          )}
                          {entry.userId && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              User: {entry.userId.name || entry.userId.phonenumber}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right font-medium whitespace-nowrap">
                          <span
                            className={`inline-flex items-center justify-end whitespace-nowrap tabular-nums ${
                              entry.direction === "debit" ? "text-red-600" : "text-green-600"
                            }`}
                          >
                            {`${entry.direction === "debit" ? "+" : "-"}${fmt(entry.amount, currency)}`}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              entry.direction === "debit"
                                ? "bg-red-100 text-red-700"
                                : "bg-green-100 text-green-700"
                            }`}
                          >
                            {entry.direction === "debit" ? "Owed" : "Paid"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {ledger.pages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Page {ledger.page} of {ledger.pages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={ledger.page <= 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(ledger.pages, p + 1))}
                    disabled={ledger.page >= ledger.pages}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── Main Ledger Page ─────────────────────────────────────────────────────────

// Master admin: settlements/adjustments/the cross-restaurant summary are
// platform-level actions/data (see server-side ledgerController) — sees the
// full restaurant list and can settle/adjust. A location_admin only has
// read access to their own restaurant's ledger.
const MasterAdminLedger: React.FC = () => {
  const [summaries, setSummaries] = useState<RestaurantSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState<RestaurantSummary | null>(null);

  const fetchSummaries = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/ledger/summary");
      if (res.data.success) setSummaries(res.data.data);
    } catch {
      toast.error("Failed to load restaurants");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSummaries(); }, [fetchSummaries]);

  if (selectedRestaurant) {
    return (
      <RestaurantLedgerView
        restaurant={selectedRestaurant}
        onBack={() => { setSelectedRestaurant(null); fetchSummaries(); }}
      />
    );
  }

  return (
    <SummaryView
      summaries={summaries}
      loading={loading}
      onSelect={setSelectedRestaurant}
      onRefresh={fetchSummaries}
    />
  );
};

const LocationAdminLedger: React.FC<{ restaurantId: string }> = ({ restaurantId }) => {
  const [restaurant, setRestaurant] = useState<RestaurantSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAdminMe()
      .then((res) => {
        const data = res.data?.data;
        setRestaurant({
          restaurantId,
          hotelName: data?.hotelName || "Your Restaurant",
          address: "",
          totalDebit: 0,
          totalCredit: 0,
          balance: 0,
          lastEntry: null,
        });
      })
      .catch(() => toast.error("Failed to load your restaurant"))
      .finally(() => setLoading(false));
  }, [restaurantId]);

  if (loading) {
    return <div className="p-12 text-center text-gray-400">Loading...</div>;
  }
  if (!restaurant) {
    return <div className="p-12 text-center text-gray-400">No restaurant found</div>;
  }

  return <RestaurantLedgerView restaurant={restaurant} readOnly />;
};

const Ledger: React.FC = () => {
  const tokenInfo = getTokenInfo();

  if (tokenInfo?.role === "master_admin") {
    return <MasterAdminLedger />;
  }

  if (!tokenInfo?.restaurantId) {
    return (
      <div className="p-12 text-center text-gray-400">
        Add your restaurant location before viewing its ledger.
      </div>
    );
  }

  return <LocationAdminLedger restaurantId={tokenInfo.restaurantId} />;
};

export default Ledger;
